import { useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import CosmicBackground from "../components/CosmicBackground";
import mockupReports from "../assets/mockup-personalized-reports.webp";
import mockupIntro from "../assets/mockup-aura-intro.webp";
import mockupVoice from "../assets/mockup-voice-chat.webp";
import heroCut from "../assets/dl-hero-full.webp";
import personA from "../assets/dl-person-woman-30s.webp";
import personB from "../assets/dl-person-man-30s.webp";
import personC from "../assets/dl-person-woman-20s.webp";
import "./DownloadPage.css";

/**
 * /foreign, the app-download campaign landing page. Written for the FOREIGN
 * audience (US, UK, UAE, Canada), so the language is English throughout and
 * the offer leads with tarot, birth chart and horoscope rather than the
 * Hinglish shaadi/kundli framing the India landings use. An India version of
 * this page is planned separately.
 *
 * NOT INDEXED, on purpose, matching /free-kundli-matching: routed and
 * prerendered, but absent from INDEXABLE, from ALLOW in public/index.html and
 * from the sitemap, so the bootstrap script stamps noindex,follow. To make it
 * indexable later, add it to all four lists together.
 *
 * THE DOWNLOAD BUTTON routing is copied from public/go.html rather than
 * reinvented, so both behave identically:
 *
 *     Android  -> Play Store
 *     iOS      -> /usa, the international landing (no iOS app exists)
 *     Desktop  -> /usa, the international landing
 *
 * The Windows Phone exclusion carries over: its UA historically contained
 * "Android" and must not be sent to Play.
 */

const PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.astroaura.auraai";

/* Where a visitor with no Android device is sent. /usa is the international
   landing, written for this same foreign audience (the NRI hub since USA
   batch 1). */
const INTERNATIONAL_URL = "/usa";

const isAndroidDevice = (): boolean => {
  const ua = navigator.userAgent || navigator.vendor || "";
  return /android/i.test(ua) && !/windows phone/i.test(ua);
};

/* What the app actually does. Every one of these is a real feature with a
   page behind it (TarotReadingsPage, the chart tools, the AI chat, the
   compatibility report), so none of it is aspirational copy. */
const FEATURES = [
  { icon: "\u{1F52E}", label: "Tarot readings", note: "Pull a spread, get it read" },
  { icon: "\u{2728}", label: "Birth chart", note: "Free, from your exact birth data" },
  { icon: "\u{1F319}", label: "Daily horoscope", note: "For your real rising sign" },
  { icon: "\u{1F495}", label: "Compatibility", note: "Two charts, read together" },
  { icon: "\u{1F3A4}", label: "Voice chat", note: "Talk to it, do not type" },
  { icon: "\u{1F4D6}", label: "Full reports", note: "Career, love, money, timing" },
];


/* In-app feedback from Aura AI users, supplied by Dhruv on 2026-10-02 together
   with the names of the users it came from. Text is used VERBATIM.

   These replaced the Google Play reviews that were here before, which came
   from Indian reviewers and so read wrong on a page bought for US traffic.

   THE NAME ON A CARD MUST BE THE PERSON WHO WROTE THAT TEXT. Names and texts
   arrived as two separate lists, so the pairing below is in the order each was
   supplied and is Dhruv's to confirm. Never attach a name to words written by
   someone else, and never invent a reviewer: a testimonial from a person who
   did not say it is exactly what the FTC endorsement rules penalise, and this
   page is aimed squarely at the US.

   No dates are shown because none were supplied. Do not fill them in. */
/* The reference page frames its offer as "for love, career or life clarity".
   Same idea, as tappable entry points rather than a static line. */
const INTENTS = ["Love", "Career", "Money", "Timing", "Family"];

/* Aries through Pisces, U+2648..U+2653, each followed by U+FE0E.

   That trailing character is load-bearing: these codepoints default to EMOJI
   presentation, so the OS was substituting its colour emoji font and the ring
   rendered as a dozen rounded tiles. U+FE0E is the text variation selector,
   which forces the thin monochrome astrological symbol instead. Do not strip
   it as invisible whitespace. */
const ZODIAC = [
  "♈︎", "♉︎", "♊︎",
  "♋︎", "♌︎", "♍︎",
  "♎︎", "♏︎", "♐︎",
  "♑︎", "♒︎", "♓︎",
];

const REVIEWS = [
  {
    name: "James Carter",
    stars: 5,
    text:
      "I’ve tried a few astrology apps, but Aura AI feels much easier to understand. The readings are detailed without being confusing, and I’ve found myself coming back to check different parts of my chart. Really interesting experience.",
  },
  {
    name: "Emma Collins",
    stars: 5,
    text:
      "I was honestly surprised by how detailed the readings were. It doesn’t just give you a general prediction, it explains the situation and the timing in a way that makes it easier to relate to your own life. Really enjoyed using it.",
  },
  {
    name: "Sophie Mitchell",
    stars: 5,
    text:
      "Aura AI has a really clean and simple interface. The astrology readings are detailed, easy to follow, and give you a different perspective on things happening in your life. Definitely worth trying if you’re into astrology.",
  },
];

const DownloadPage = () => {
  const navigate = useNavigate();

  const handleDownload = useCallback(() => {
    if (isAndroidDevice()) {
      // Leaving the site entirely, so a full assignment rather than the router.
      window.location.href = PLAY_URL;
      return;
    }
    // iOS and desktop: there is no iOS app, so instead of dropping them on the
    // generic home page they go to the INTERNATIONAL landing, which is written
    // for this same audience.
    //
    // Both this page and /usa sit behind <InternationalRoute>, so anyone who
    // reaches this CTA has already cleared the geo gate and will clear the one
    // on /usa too. The hop cannot bounce them home.
    navigate(INTERNATIONAL_URL);
  }, [navigate]);

  return (
    <div className="dl-root">
      <CosmicBackground />
      {/* Page-local sky. CosmicBackground is deliberately blurred by 1.4px so
          foreground content reads as solid; this layer puts a few CRISP stars
          and a zodiac ring back on top, which is what stops the page reading
          as a flat purple panel. */}
      <div className="dl-sky" aria-hidden="true">
        <div className="dl-sky__far" />
        <div className="dl-sky__mid" />
        <div className="dl-sky__near" />
      </div>

      <Helmet>
        <title>Download Aura AI: Tarot, Birth Chart &amp; Horoscope App</title>
        <meta
          name="description"
          content="Tarot readings, your free birth chart, daily horoscope and an AI astrologer you can talk to. First reading free."
        />
      </Helmet>

      <main className="dl-main">
        {/* HERO */}
        <section className="dl-hero">
          {/* Marketing page, not an SEO page. The headline and body paragraph
              that used to sit here pushed the product shot below the fold for
              no benefit. The reference leads with the image and puts its one
              line AFTER the stats, so that is what happens here too. */}
          {/* No card. The hero is an alpha CUTOUT standing on the page's own
              sky, which is the only way the boundary actually disappears: a
              rectangle's edge always shows as a tonal seam against the page
              gradient, however softly it is faded. The brand block sits above
              her on that same sky. */}
          <div className="dl-stage">
            <span className="dl-halo" aria-hidden="true" />

            {/* Zodiac rings behind her. The hero on a wide screen was a narrow
                column of content in an empty purple field; this is the backdrop
                that fills it. Drawn as SVG rather than shipped as an image so
                it scales to any width for free and costs no bytes. */}
            <svg
              className="dl-rings"
              viewBox="0 0 400 400"
              aria-hidden="true"
              focusable="false"
            >
              {/* Circles are STATIC. Rotating them produced a visible shimmer
                  along the hairlines for no gain: a rotating circle is
                  indistinguishable from a still one. Only the glyphs turn. */}
              <g className="dl-rings__fixed">
                <circle cx="200" cy="200" r="162" />
                <circle cx="200" cy="200" r="132" />
                <circle cx="200" cy="200" r="102" />
              </g>
              <g className="dl-rings__spin">
                {ZODIAC.map((glyph, i) => {
                  // 12 signs, evenly spaced, starting at the top.
                  const a = (i / ZODIAC.length) * Math.PI * 2 - Math.PI / 2;
                  return (
                    <text
                      key={glyph}
                      x={200 + Math.cos(a) * 147}
                      y={200 + Math.sin(a) * 147}
                      dominantBaseline="central"
                      textAnchor="middle"
                    >
                      {glyph}
                    </text>
                  );
                })}
              </g>
            </svg>

            <div className="dl-brand">
              <img
                className="dl-brand__logo"
                src="/logo.svg"
                alt=""
                width={92}
                height={92}
                aria-hidden="true"
              />
              <p className="dl-brand__name">Aura AI</p>
              <p className="dl-brand__offer">First reading free</p>
            </div>

            <img
              className="dl-cut"
              src={heroCut}
              alt="Someone holding a phone showing the Aura AI app"
              width={330}
              height={506}
              loading="eager"
            />
          </div>

          {/* The page's single h1, directly under the image now that the stats
              card has moved down to the reviews. */}
          <div className="dl-tag">
            <h1>Get a straight answer</h1>
            <p>for love, career, money or timing</p>

            <div className="dl-faces" aria-hidden="true">
              <img src={personA} alt="" width={120} height={120} loading="lazy" />
              <img src={personB} alt="" width={120} height={120} loading="lazy" />
              <img src={personC} alt="" width={120} height={120} loading="lazy" />
            </div>
          </div>
        </section>

        {/* WHAT IS IN IT */}
        <section className="dl-block">
          <h2>Everything in one app</h2>
          <ul className="dl-feats">
            {FEATURES.map((f) => (
              <li key={f.label}>
                <span className="dl-feats__icon" aria-hidden="true">{f.icon}</span>
                <b>{f.label}</b>
                <span className="dl-feats__note">{f.note}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* HOW. The reference layout has nothing like this, but an app
            landing page that never says what happens after you tap install is
            asking for a lot on faith. Replaces the four trust cards; the trust
            points survive as the compact strip below. */}
        <section className="dl-block">
          <h2>How it works</h2>
          <ol className="dl-steps">
            <li>
              <b>Enter your birth details</b>
              <span>Date, time and place. Takes under a minute.</span>
            </li>
            <li>
              <b>Your chart gets computed</b>
              <span>From real astronomy data, not a sun-sign lookup.</span>
            </li>
            <li>
              <b>Ask, or pull a spread</b>
              <span>Answers cite the placement they came from.</span>
            </li>
          </ol>
        </section>

        {/* REVIEWS. Real Play Store reviews only, see REVIEWS above. */}
        <section className="dl-block">
          {/* Read off the live Play Store listing on 2026-10-01: 4.4 from
              1.69K ratings. The download tier is 1M+ (10L+), confirmed by
              Dhruv on 2026-10-02. Since the 2026-10-10 claims sweep the rest
              of the site states these same figures; if a discrepancy ever
              reappears, the Play listing is the source of truth. */}
          {/* Clickable: the numbers are the proof, so tapping them should do
              the thing they are proof of. Same device routing as the main CTA
              rather than a hard Play link, so an iOS visitor is not sent to a
              listing for an app they cannot install. */}
          <button className="dl-stats" type="button" onClick={handleDownload}>
            <span><b>4.4</b><span>on Google Play</span></span>
            <span><b>1M+</b><span>downloads</span></span>
            <span><b>1.7K</b><span>ratings</span></span>
          </button>

          {/* The old dl-score block lived here and also led with 4.4, so the
              same rating was stated twice in one section. The stats card above
              covers it; only the stars are kept. */}
          {/* The three app screens live here now, not in the hero. Above the
              fold they competed with the product shot; beside the ratings they
              are evidence for it. */}
          <div className="dl-shots" aria-label="Screens from the Aura AI app">
            <img src={mockupIntro} alt="Aura AI chat screen" loading="lazy" width={200} height={420} />
            <img src={mockupReports} alt="Personalised report screen" loading="lazy" width={200} height={420} />
            <img src={mockupVoice} alt="Voice chat screen" loading="lazy" width={200} height={420} />
          </div>

          <ul className="dl-revs">
            {REVIEWS.map((r) => (
              <li key={r.name}>
                <span className="dl-revs__mark" aria-hidden="true">
                  {"\u201c"}
                </span>
                <p>{r.text}</p>
                <div className="dl-revs__foot">
                  <span className="dl-revs__av" aria-hidden="true">
                    {r.name.charAt(0)}
                  </span>
                  <span className="dl-revs__who">
                    <b>{r.name}</b>
                    {/* No date line: none were supplied with this feedback, and
                        a plausible-looking invented date is still invented. */}
                    <span>Aura AI user</span>
                  </span>
                  <span
                    className="dl-revs__rate"
                    aria-label={`${r.stars} out of 5`}
                  >
                    {"\u2605".repeat(r.stars)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* TRUST STRIP. Three reassurances, divided, closing the page above
            the CTA.

            The reference layout leads with "Verified Psychics". We cannot say
            that: Aura AI is an AI with no human readers, so a claim about
            vetted psychics would be false. Each line here is something the
            product actually does and the page already backs up: charts are
            computed from real ephemeris data (see "How it works"), chats are
            private, and payments go through the hosted gateway. */}
        <ul className="dl-trust">
          <li>
            <span className="dl-trust__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="9" />
                <circle cx="12" cy="12" r="3.4" />
                <path d="M12 3v3.6M12 17.4V21M3 12h3.6M17.4 12H21" />
              </svg>
            </span>
            <b>Real birth charts</b>
          </li>
          <li>
            <span className="dl-trust__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M20 14.5a2.5 2.5 0 0 1-2.5 2.5H9l-4 3.2V6.5A2.5 2.5 0 0 1 7.5 4h10A2.5 2.5 0 0 1 20 6.5z" />
                <rect x="9.5" y="9.4" width="6" height="4.4" rx="1" />
                <path d="M10.9 9.4V8.2a1.6 1.6 0 0 1 3.2 0v1.2" />
              </svg>
            </span>
            <b>Private readings</b>
          </li>
          <li>
            <span className="dl-trust__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
                <path d="M2.5 10h19" />
                <path d="M6 14.4h4" />
              </svg>
            </span>
            <b>Secure payments</b>
          </li>
        </ul>
      </main>

      {/* Sticky CTA. The one action on the page. */}
      <div className="dl-bar">
        <button className="dl-cta" type="button" onClick={handleDownload}>
          Get Aura AI, first reading free
        </button>
      </div>
    </div>
  );
};

export default DownloadPage;
